/**
 * Utility functions for handling box sleeve functionality
 */

import { GIFT_BOX_SLEEVE_PREFIX } from "../constants/product";

/**
 * Determines if a given option group is a gift box sleeve option.
 * @param {Object} option - The option group object.
 * @returns {boolean} - True if the option is a gift box sleeve option, false otherwise.
 */
export const isGiftBoxSleeveOption = option => {
  return (
    option.groupTitle?.startsWith(GIFT_BOX_SLEEVE_PREFIX) ||
    option.header?.startsWith(GIFT_BOX_SLEEVE_PREFIX)
  );
};

/**
 * Gets the sleeve option group from a product's options
 * @param {Object} product - The product object
 * @returns {Object|null} - The sleeve option group or null if none found
 */
export const getSleeveOptions = product => {
  if (!product?.options) {
    return null;
  }

  return product.options.find(isGiftBoxSleeveOption) || null;
};

/**
 * Checks if a product has sleeve options available
 * @param {Object} product - The product object
 * @returns {boolean} - True if product has sleeve options
 */
export const hasSleeveOptions = product => {
  return !!getSleeveOptions(product);
};

/**
 * Filters out sleeve options from a product's options array
 * @param {Array} options - Array of product option groups
 * @returns {Array} - Filtered options array without sleeve options
 */
export const filterOutSleeveOptions = options => {
  if (!Array.isArray(options)) {
    return options;
  }

  return options.filter(option => !isGiftBoxSleeveOption(option));
};

/**
 * Gets only the sleeve options from a product's options array
 * @param {Array} options - Array of product option groups
 * @returns {Array} - Array containing only sleeve option groups
 */
export const getOnlySleeveOptions = options => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter(isGiftBoxSleeveOption);
};

/**
 * Counts total number of sleeve options available for a product
 * @param {Object} product - The product object
 * @returns {number} - Number of sleeve options available
 */
export const getSleeveCount = product => {
  const sleeveOptions = getSleeveOptions(product);
  return sleeveOptions?.options?.length || 0;
};

export const hasCategorySlug = (product, slug) => {
  if (!Array.isArray(product.categories)) {
    return false;
  }

  if (Array.isArray(slug)) {
    return slug.some(s => hasCategorySlug(product, s));
  }

  return product.categories?.some(
    category => `${category?.slug}`.toLowerCase() === slug.toLowerCase()
  );
};

/**
 * Checks if sleeve options are available and have at least one option in stock
 * @param {Object} product - The product object
 * @returns {boolean} - True if sleeve options exist and at least one is in stock
 */
export const hasAvailableSleeveOptions = product => {
  const sleeveOptions = getSleeveOptions(product);
  if (!sleeveOptions?.options) {
    return false;
  }

  // Check if at least one sleeve option is in stock (oos !== true)
  return sleeveOptions.options.some(option => !option.oos);
};

/**
 * Determines if a product is a box product (i.e., has sleeve options).
 * @param {Object} product - The product object.
 * @returns {boolean} - True if the product is a box product, false otherwise.
 */
export const isBoxProduct = product => {
  return (
    getSleeveOptions(product) !== null &&
    !hasCategorySlug(product, ["cake", "cakes"])
  );
};
